'use client';

import { useEffect } from 'react';

import { useBiLoggerContext } from '@/components/Contexts/BiLoggerContext';
import { useCommentsContext } from '@/components/Contexts/CommentsContext';
import { useViewerRole } from '@/components/Contexts/ViewerRoleContext';
import { useWorkbenchConfig } from '@/components/Contexts/WorkbenchConfigContext';
import { PinPopover } from '@/components/PinPopover/PinPopover';
import { BI_CLICK } from '@/constants/bi';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { pinCommentHasEmail } from '@/utils/bi';

import { SIDEBAR_PANEL_WIDTH } from './WorkbenchSidebarPanel';

import type { WorkbenchPinPopoverModel } from './types';
import type { CommentUserInfo } from '@/types/feedback';

interface WorkbenchPinPopoverLayerProps {
  model: WorkbenchPinPopoverModel | null;
  commenterInfoMap: Record<string, CommentUserInfo>;
  sidebarVisible?: boolean;
  onDismiss: () => void;
  onSave: () => void;
  onUpdatePinLabel: (id: string, label: string) => void;
  onDeletePin: (id: string) => void;
  onResolvePin?: (id: string) => void;
  onReopenPin?: (id: string) => void;
  onAddReply?: (pinId: string, message: string) => void;
  onDeleteReply?: (pinId: string, replyIndex: number) => void;
}

export function WorkbenchPinPopoverLayer({
  model,
  commenterInfoMap,
  sidebarVisible,
  onDismiss,
  onSave,
  onUpdatePinLabel,
  onDeletePin,
  onResolvePin,
  onReopenPin,
  onAddReply,
  onDeleteReply,
}: WorkbenchPinPopoverLayerProps) {
  useEffect(() => {
    if (!model) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [model]);

  const { isPartner } = useViewerRole();
  const { reportClick } = useBiLoggerContext();
  const {
    state: { visitorId },
  } = useCommentsContext();
  const { data: apiUser } = useCurrentUser();
  const { agencyName } = useWorkbenchConfig();

  if (!model) return null;

  const pin = model.pin;
  const commentHasEmail = pinCommentHasEmail(pin, commenterInfoMap);

  return (
    <>
      {/* Backdrop: blurs page content and blocks all interaction (partner only) */}
      {!model.pin.commentId && isPartner && (
        <div
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          className="backdrop-blur-sm bg-black/20"
        />
      )}
      <PinPopover
        // Remount when the selected pin changes so PinPopover's internal
        // isEditing/menu state resets instead of leaking from whatever pin
        // was open before (e.g. a fresh unsaved pin silently inheriting the
        // previous pin's "view mode", hiding its Save button).
        key={model.pin.id}
        pin={model.pin}
        pinIndex={model.pinIndex}
        commenterInfoMap={commenterInfoMap}
        agencyName={agencyName}
        anchorX={model.anchorX}
        anchorY={model.anchorY}
        rightInset={sidebarVisible ? SIDEBAR_PANEL_WIDTH : 0}
        onClose={onDismiss}
        onSave={() => {
          reportClick({
            clickType: pin.commentId
              ? BI_CLICK.COMMENT_EDIT
              : BI_CLICK.COMMENT_ADD,
            commentId: pin.commentId,
            text: pin.label,
            commentHasEmail,
          });
          onSave();
        }}
        onUpdateLabel={(label) => onUpdatePinLabel(pin.id, label)}
        canDelete={!isPartner}
        canEdit={!isPartner}
        onDelete={() => {
          reportClick({
            clickType: BI_CLICK.COMMENT_DELETE,
            commentId: pin.commentId,
            commentHasEmail,
          });
          onDeletePin(pin.id);
          onDismiss();
        }}
        onResolve={
          isPartner && onResolvePin
            ? () => {
                reportClick({
                  clickType: BI_CLICK.COMMENT_RESOLVE,
                  commentId: pin.commentId,
                  commentHasEmail,
                });
                onResolvePin(pin.id);
                onDismiss();
              }
            : undefined
        }
        onReopen={
          onReopenPin
            ? () => {
                reportClick({
                  clickType: BI_CLICK.COMMENT_REOPEN,
                  commentId: pin.commentId,
                  commentHasEmail,
                });
                onReopenPin(pin.id);
                onDismiss();
              }
            : undefined
        }
        onAddReply={
          onAddReply
            ? (message) => {
                reportClick({
                  clickType: BI_CLICK.REPLY_ADD,
                  commentId: pin.commentId,
                  commentHasEmail,
                  text: message,
                });
                onAddReply(pin.id, message);
              }
            : undefined
        }
        onDeleteReply={
          onDeleteReply
            ? (replyIndex) => {
                reportClick({
                  clickType: BI_CLICK.REPLY_DELETE,
                  commentId: pin.commentId,
                  commentHasEmail,
                  responseId: String(replyIndex),
                });
                onDeleteReply(pin.id, replyIndex);
              }
            : undefined
        }
        currentUserId={isPartner ? apiUser?.id : visitorId}
      />
    </>
  );
}
